import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Ported verbatim from the main app's components/PaginationBar.js (19.3's client
// paragraph: "PaginationBar with [25, 50, 100]"). Generic prev/next bar for a
// server-paginated list; no project-specific logic lives here.
export default function PaginationBar({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  itemLabel = 'results',
  compact = false,
  className = '',
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total || 0);

  if (!total && total !== 0) return null;

  const summary = total > 0 ? (
    <>
      Showing{' '}
      <span className="font-semibold text-gray-900 tabular-nums">{start.toLocaleString()}</span>
      <span className="mx-1 text-gray-400" aria-hidden="true">–</span>
      <span className="font-semibold text-gray-900 tabular-nums">{end.toLocaleString()}</span>
      {' of '}
      <span className="font-semibold text-gray-900 tabular-nums">{total.toLocaleString()}</span>
      {' '}
      <span className="text-gray-500">{itemLabel}</span>
    </>
  ) : (
    <span>No {itemLabel}</span>
  );

  if (compact) {
    return (
      <nav
        aria-label="Pagination"
        className={`border-t border-gray-100 pt-4 text-sm text-gray-600 ${className}`}
      >
        <p className="text-sm text-gray-600">{summary}</p>

        <div className="mt-3 flex items-center justify-between gap-3">
          {onPageSizeChange && pageSizeOptions.length > 0 ? (
            <label className="inline-flex items-center gap-2 text-sm text-gray-500">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                aria-label="Results per page"
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm font-medium text-gray-900 tabular-nums shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          ) : (
            <span />
          )}

          <div className="inline-flex items-stretch overflow-hidden rounded-md bg-white shadow-sm ring-1 ring-gray-200">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
              aria-label="Previous page"
              className="inline-flex items-center justify-center px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="inline-flex items-center gap-1 border-x border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-600 tabular-nums">
              <span className="font-semibold text-gray-900">{safePage.toLocaleString()}</span>
              <span className="text-gray-400">/</span>
              <span className="font-semibold text-gray-900">{totalPages.toLocaleString()}</span>
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
              aria-label="Next page"
              className="inline-flex items-center justify-center px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Pagination"
      className={`text-sm text-gray-600 ${className}`}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600 leading-relaxed">{summary}</p>

        <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-2">
          {onPageSizeChange && pageSizeOptions.length > 0 && (
            <label className="inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap">
              <span className="text-gray-500">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                aria-label="Results per page"
                className="min-w-[4.5rem] rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-900 tabular-nums shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          )}

          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-gray-50 px-3 py-1.5 text-sm text-gray-600 ring-1 ring-gray-200">
            <span>Page</span>
            <span className="font-semibold text-gray-900 tabular-nums">{safePage.toLocaleString()}</span>
            <span>of</span>
            <span className="font-semibold text-gray-900 tabular-nums">{totalPages.toLocaleString()}</span>
          </span>

          <div className="inline-flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
              aria-label="Previous page"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              <ChevronLeftIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">Previous</span>
            </button>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
              aria-label="Next page"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
